import React from "react";
import { Button, Result } from "antd";

type Props = {
  children: React.ReactNode;
  /** Shown instead of the default panel. Receives the reset handler. */
  fallback?: (reset: () => void) => React.ReactNode;
};

type State = { error: Error | null };

/**
 * Catches render-time exceptions so one bad record cannot blank the whole app.
 *
 * Without this, a single unexpected shape from the API — a null message array, a
 * malformed date — unmounts the entire React tree and the user sees a white page
 * with no way back.
 */
class ErrorBoundary extends React.Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: React.ErrorInfo) {
    // Replace with the real error reporter when one is wired up.
    console.error("Unhandled render error", error, info.componentStack);
  }

  reset = () => this.setState({ error: null });

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;
    if (this.props.fallback) return this.props.fallback(this.reset);

    return (
      <Result
        status="error"
        title="Something went wrong"
        subTitle={error.message}
        extra={[
          <Button key="retry" type="primary" onClick={this.reset}>
            Try again
          </Button>,
          <Button key="reload" onClick={() => window.location.reload()}>
            Reload page
          </Button>,
        ]}
      />
    );
  }
}

export default ErrorBoundary;
